import { useEffect, useRef } from "react";
import { DEFAULT_AURORA, useConfigStore } from "../../Stores/Config";

interface KaiVoiceParticlesProps {
  /** 0..1 live audio level — the cluster breathes with the voice. */
  getLevel: () => number;
  /** Kai is working on an answer: the sphere gathers with a hint of spiral (see THINK_*). */
  thinking?: boolean;
  /** Harness mode: seeded layout + a single static frame, so VR runs are deterministic. */
  frozen?: boolean;
}

// Tiny deterministic PRNG for frozen (VR) rendering.
const createSeededRandom = (seed: number) => () => {
  seed = (seed * 1664525 + 1013904223) % 4294967296;
  return seed / 4294967296;
};

interface Particle {
  /** Unit-sphere position — rotated in 3D each frame and projected. */
  x: number;
  y: number;
  z: number;
  size: number;
  twinklePhase: number;
  twinkleSpeed: number;
  color: string;
  /** 0..THINK_PHASE_SPREAD cycle offset — stars gather slightly out of step, not as one rigid shell. */
  thinkPhase: number;
}

interface AmbientParticle {
  /** 0..1 canvas-relative position — the sparse far stars around the cluster. */
  u: number;
  v: number;
  size: number;
  twinklePhase: number;
  alpha: number;
}

const SPHERE_PARTICLE_COUNT = 340;
const AMBIENT_PARTICLE_COUNT = 46;
// "Thinking": the sphere revolves visibly (one turn every ~14 s, a loading
// spinner made of stars) while breathing 20% inward in a 3.8 s cycle with a
// faint mint core at the most compressed moment; stars stay white. The spin
// speed and the breath ease in and out over ~0.7 s, so the sphere winds up
// and coasts down instead of snapping.
const THINK_CYCLE_MS = 3800;
const THINK_CONTRACTION = 0.2;
const THINK_SPIN_RAD_PER_MS = (Math.PI * 2) / 14000;
const THINK_PHASE_SPREAD = 0.18;
const THINK_CORE_ALPHA = 0.4;
const THINK_CORE_COLOR = "140, 232, 214";
const THINK_EASE = 0.06;
// How far (0..1) a color moves toward white — keeps the brand tint present
// but subdued; saturated confetti dots read as playful, not professional.
const TINT_SOFTEN = 0.72;

const hexToRgb = (hex: string): [number, number, number] | null => {
  const normalized = hex.replace("#", "");
  const value =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)];
};

const softenTowardWhite = (hex: string, amount: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return "rgb(235, 240, 238)";
  const [r, g, b] = rgb.map((channel) => Math.round(channel + (255 - channel) * amount));
  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * The Fin-style "voice presence": a dense, slowly revolving sphere of fine
 * stardust that breathes with the live audio level, over a few sparse ambient
 * stars. 3D points projected with depth-scaled size/alpha — the depth cue is
 * what makes it read as a calm volumetric presence instead of confetti.
 * Pure canvas — no deps — softly tinted from the project's aurora palette.
 */
export const KaiVoiceParticles = ({ getLevel, thinking = false, frozen }: KaiVoiceParticlesProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const getLevelRef = useRef(getLevel);
  getLevelRef.current = getLevel;
  const thinkingRef = useRef(thinking);
  thinkingRef.current = thinking;

  const auroraColors = useConfigStore((state) => state.getAuroraColors?.() ?? undefined);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    const palette = (auroraColors && auroraColors.length > 0 ? auroraColors : DEFAULT_AURORA).map(
      (color: string) => softenTowardWhite(color, TINT_SOFTEN)
    );
    const white = "rgb(240, 244, 242)";
    const reducedMotion =
      frozen || (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false);
    const random = frozen ? createSeededRandom(1337) : Math.random;

    const particles: Particle[] = Array.from({ length: SPHERE_PARTICLE_COUNT }, () => {
      // Uniform direction; radius biased outward for a full core with a
      // defined (but soft) spherical edge.
      const theta = random() * Math.PI * 2;
      const cosPhi = random() * 2 - 1;
      const sinPhi = Math.sqrt(Math.max(0, 1 - cosPhi * cosPhi));
      const radius = Math.pow(random(), 0.42);
      return {
        x: sinPhi * Math.cos(theta) * radius,
        y: cosPhi * radius,
        z: sinPhi * Math.sin(theta) * radius,
        size: 0.5 + random() * 0.8,
        twinklePhase: random() * Math.PI * 2,
        twinkleSpeed: 0.5 + random(),
        // Mostly near-white dust; roughly a third carries the soft brand tint.
        color: random() < 0.34 ? palette[Math.floor(random() * palette.length)] : white,
        thinkPhase: random() * THINK_PHASE_SPREAD,
      };
    });

    const ambient: AmbientParticle[] = Array.from({ length: AMBIENT_PARTICLE_COUNT }, () => ({
      u: random(),
      v: random(),
      size: 0.4 + random() * 0.7,
      twinklePhase: random() * Math.PI * 2,
      alpha: 0.08 + random() * 0.2,
    }));

    let rafId: number | null = null;
    let smoothedLevel = 0;
    // 0 = calm sphere, 1 = fully in the thinking breath (eased toward the flag).
    let thinkAmount = 0;
    // Accumulated extra revolution from thinking — keeps its angle when
    // thinking ends, so the stars never jump back.
    let thinkSpin = 0;
    let lastTime = 0;

    const draw = (time: number) => {
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
      }

      context.clearRect(0, 0, width, height);

      const level = Math.min(Math.max(getLevelRef.current() ?? 0, 0), 1);
      smoothedLevel += (level - smoothedLevel) * 0.12;
      thinkAmount += ((thinkingRef.current && !reducedMotion ? 1 : 0) - thinkAmount) * THINK_EASE;
      if (thinkAmount < 0.002) thinkAmount = 0;
      const dt = lastTime ? Math.min(time - lastTime, 50) : 0;
      lastTime = time;
      thinkSpin += dt * THINK_SPIN_RAD_PER_MS * thinkAmount;
      const thinkPhase = (time % THINK_CYCLE_MS) / THINK_CYCLE_MS;
      // Whole-sphere breath (0 → 1 → 0 per cycle) for the core glow.
      const thinkWave = (1 - Math.cos(thinkPhase * Math.PI * 2)) / 2;

      const centerX = width / 2;
      const centerY = height / 2;
      const baseRadius = Math.min(width, height) * 0.34;
      const clusterRadius = baseRadius * (1 + smoothedLevel * 0.16);

      if (thinkAmount > 0) {
        const coreAlpha = THINK_CORE_ALPHA * thinkWave * thinkAmount;
        const coreRadius = baseRadius * (0.55 + 0.25 * thinkWave);
        const glow = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreRadius);
        glow.addColorStop(0, `rgba(${THINK_CORE_COLOR}, ${coreAlpha.toFixed(3)})`);
        glow.addColorStop(0.55, `rgba(${THINK_CORE_COLOR}, ${(coreAlpha * 0.25).toFixed(3)})`);
        glow.addColorStop(1, `rgba(${THINK_CORE_COLOR}, 0)`);
        context.globalAlpha = 1;
        context.fillStyle = glow;
        context.beginPath();
        context.arc(centerX, centerY, coreRadius, 0, Math.PI * 2);
        context.fill();
      }

      // Ambient far stars — static positions, faint slow twinkle.
      for (const star of ambient) {
        const twinkle = reducedMotion
          ? 0.7
          : 0.55 + 0.45 * Math.sin(time * 0.00035 + star.twinklePhase);
        context.globalAlpha = star.alpha * twinkle;
        context.fillStyle = white;
        context.beginPath();
        context.arc(star.u * width, star.v * height, star.size, 0, Math.PI * 2);
        context.fill();
      }

      // Barely-perceptible revolve around Y with a gentle fixed tilt — the
      // presence should shimmer in place, not read as a spinning globe.
      const rotation = (reducedMotion ? 0.6 : time * 0.000022) + thinkSpin;
      const sinR = Math.sin(rotation);
      const cosR = Math.cos(rotation);
      const tilt = 0.32;
      const sinT = Math.sin(tilt);
      const cosT = Math.cos(tilt);

      for (const particle of particles) {
        // Thinking: this star's own point in the breath — how far it has
        // gathered toward the core (the revolution itself is in `rotation`).
        let gather = 0;
        if (thinkAmount > 0) {
          const wave = (1 - Math.cos((thinkPhase - particle.thinkPhase) * Math.PI * 2)) / 2;
          gather = wave * thinkAmount;
        }
        const contraction = 1 - THINK_CONTRACTION * gather;

        // Rotate around Y…
        const rx = particle.x * cosR + particle.z * sinR;
        const rz = -particle.x * sinR + particle.z * cosR;
        // …then tilt around X.
        const ry = particle.y * cosT - rz * sinT;
        const rz2 = particle.y * sinT + rz * cosT;

        // Perspective: nearer points (rz2 → 1) draw larger and brighter.
        const depth = (rz2 + 1) / 2;
        const perspective = 0.72 + depth * 0.5;

        const x = centerX + rx * clusterRadius * contraction * perspective;
        const y = centerY + ry * clusterRadius * 0.96 * contraction * perspective;

        const twinkle = reducedMotion
          ? 0.5 + 0.5 * Math.abs(Math.sin(particle.twinklePhase))
          : 0.5 + 0.5 * Math.sin(time * 0.00032 * particle.twinkleSpeed + particle.twinklePhase);

        // Narrow flicker band — stars breathe, they don't blink.
        context.globalAlpha =
          (0.14 + 0.5 * depth) * (0.72 + 0.28 * twinkle) * (1 + smoothedLevel * 0.25 + gather * 0.15);
        context.fillStyle = particle.color;
        context.beginPath();
        context.arc(x, y, particle.size * perspective * (1 + smoothedLevel * 0.18), 0, Math.PI * 2);
        context.fill();
      }
      context.globalAlpha = 1;

      if (!reducedMotion) {
        rafId = requestAnimationFrame(draw);
      }
    };

    rafId = requestAnimationFrame(draw);
    return () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [auroraColors, frozen]);

  return <canvas className="kai-voice-call--particles" ref={canvasRef} />;
};
